import React, { InputHTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import {InputBox, Label, Unit, Slider, SliderWrap, FieldWrapperSlider, ErrorText } from 'components/ImageEditor/Fields/style';
import Input from 'components/Basic/Input';
import { FieldWrapper } from './styles';
type BaseInputProps = InputHTMLAttributes<HTMLInputElement>;
interface TreeSliderInputFieldProps extends BaseInputProps {
	label: string;
	unit?: string;
	showSlider?: boolean;
	isActive?: boolean;
	onActivate?: () => void;
	onInputChange: (val: number) => void;
	showError?: boolean;
	onErrorChange?:(e: boolean)=>void;
	inputMin?: number;
	inputMax?: number;
	sliderMin?: number;
	sliderMax?: number;
}

const TreeSliderInputField = (props: TreeSliderInputFieldProps) => {
	const {
		id,
		label,
		value,
		min,
		max,
		step = 1,
		unit,
		showSlider = true,
		onInputChange,
		disabled = false,
		onActivate,
		isActive,
		showError = true,
		onErrorChange,
		inputMin,
		inputMax,
		sliderMin,
		sliderMax
	} = props;

	const [localValue, setLocalValue] = useState<string>(String(value));
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const isEditingRef = useRef(false);

	const resolvedInputMin = inputMin ?? min;
	const resolvedInputMax = inputMax ?? max;
	const resolvedSliderMin = sliderMin ?? min;
	const resolvedSliderMax = sliderMax ?? max;

	const validate = (num: number) => {
		if (isNaN(num)) {
			setErrorMessage('Invalid number');
			return true;
		}

		if (showError && resolvedInputMin !== undefined && num < Number(resolvedInputMin)) {
			setErrorMessage(`Must be >= ${resolvedInputMin}`);
			return true;
		}

		if (showError && resolvedInputMax !== undefined && num > Number(resolvedInputMax)) {
			setErrorMessage(`Must be <= ${resolvedInputMax}`);
			return true;
		}

		setErrorMessage('');
		return false;
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		// Reject a keystroke/paste that would push past 1 decimal place,
		// rather than accepting then rounding it away later.
		if (val !== '' && !/^\d*\.?\d?$/.test(val)) return;
		setLocalValue(val);

		if (val.trim() === '') {
			setIsError(false);
			setErrorMessage('');
			onErrorChange?.(false);
			return;
		}

		const num = Number(val);
		const error = validate(num);
		setIsError(error);
		onErrorChange?.(error);

		if (!error) {
			onInputChange(num);
		}
	};

	const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		let num = Number(e.target.value);
		const maxNum = resolvedSliderMax !== undefined ? Number(resolvedSliderMax) : undefined;
		const stepNum = Number(step);
		if (maxNum !== undefined && Number.isFinite(maxNum) && Number.isFinite(stepNum) && stepNum > 0) {
			if (num >= maxNum - stepNum / 2) {
				num = maxNum;
			}
		}
		if (Number.isFinite(num)) num = Math.round(num);
		setIsError(false);
		onErrorChange?.(false);
		setErrorMessage('');
		setLocalValue(String(num));
		onInputChange(num);
	}, [onErrorChange, onInputChange, resolvedSliderMax, step]);

	const handleInputFocus = () => {
		isEditingRef.current = true;
		onActivate?.();
	};

	const handleInputBlur = () => {
		isEditingRef.current = false;

		const clearErrorState = () => {
			setIsError(false);
			onErrorChange?.(false);
			setErrorMessage('');
		};
		const resetToPropValue = () => {
			clearErrorState();
			setLocalValue(String(value));
		};

		const trimmed = localValue.trim();
		const num = Number(trimmed);
		if (trimmed === '' || !Number.isFinite(num)) return resetToPropValue();

		const minNum = resolvedInputMin !== undefined ? Number(resolvedInputMin) : undefined;
		const maxNum = resolvedInputMax !== undefined ? Number(resolvedInputMax) : undefined;
		let clamped = num;
		if (minNum !== undefined && Number.isFinite(minNum)) clamped = Math.max(minNum, clamped);
		if (maxNum !== undefined && Number.isFinite(maxNum)) clamped = Math.min(maxNum, clamped);

		if (clamped !== num) {
			clearErrorState();
			setLocalValue(String(clamped));
			onInputChange(clamped);
		}
	};

	useEffect(() => {
		if (!isEditingRef.current) {
			setLocalValue(String(value));
		}
	}, [value]);

	const isBlank = localValue.trim() === '';
	const roundedValue = Number.isFinite(Number(value)) ? Math.round(Number(value)) : Number(resolvedSliderMin ?? 0);
	const sliderValue = isBlank
		? Number(resolvedSliderMin ?? 0)
		: Math.min(
			Math.max(roundedValue, Number(resolvedSliderMin ?? 0)),
			Number(resolvedSliderMax ?? roundedValue)
		);

	return (
		<FieldWrapperSlider className={`${isActive ? 'active' : ''} ${showError && isError ? 'error' : ''}`}>
			<FieldWrapper>
				<Label>{label}</Label>
				<InputBox onClick={onActivate}>
					<Input
						id={id}
						type="number"
						value={localValue}
						min={resolvedInputMin}
						max={resolvedInputMax}
						step="any"
						disabled={disabled}
						onChange={handleInputChange}
						onFocus={handleInputFocus}
						onBlur={handleInputBlur}
					/>
					{unit && <Unit>{unit}</Unit>}
				</InputBox>
			</FieldWrapper>
			{showError && isError && errorMessage && (
				<ErrorText>{errorMessage}</ErrorText>
			)}
			{(showSlider && isActive) && (
				<SliderWrap>
					<Slider
						type="range"
						min={resolvedSliderMin}
						max={resolvedSliderMax}
						step={step}
						value={sliderValue}
						$value={Number(sliderValue)}
						$min={Number(resolvedSliderMin ?? 0)}
						$max={Number(resolvedSliderMax ?? 0)}
						onChange={handleSliderChange}
						disabled={disabled}
					/>
				</SliderWrap>
			)}
		</FieldWrapperSlider>	
	);
};

export default TreeSliderInputField;
